import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@material-ui/core';

import { useBackendContext } from '@omq/shared';

import { BASE_URL } from '@admin/conf';

import { LoginAPI } from '@admin/shared/api/login';
import { Headline } from '@admin/shared/components/typography/headline';
import { Form, FormRow } from '@admin/shared/components/forms/form';
import { TextField } from '@admin/shared/components/inputs/text-field/text-field';
import { DEFAULT_REDIRECT_LOCATION } from '@admin/login/components/login-form/login-form';
import type { ValidationState } from '@admin/shared/util/validation';
import { Validation } from '@admin/shared/util/validation';

// config of form validation
const validationConfig = {
  totp: {
    isRequired: true,
    pattern: /^[0-9]{6}$/,
  },
};

/**
 * Renders TOTP form.
 *
 * @author Jonas Gutenschwager
 * @since 10.4
 *
 * @returns {JSX.Element}
 */
export const TotpForm = ({ setup = false }: { setup?: boolean }): JSX.Element => {
  const { t } = useTranslation(['login']);

  // set up states
  const [totp, setTotp] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [validationState, setValidationState] = useState<ValidationState>({});
  const backend = useBackendContext();

  useEffect(() => {
    if (setup) {
      // check if QR CODE is needed: if not -> redirect to login page
      LoginAPI.qrcode(backend)
        .then(() => {
          // nothing
        })
        .catch((ex) => {
          console.log('error', ex);
          if (ex.response.status === 403 || ex.response.status === 401) {
            (window as Window).location = `/${BASE_URL}/login`;
          }
        });
    } else {
      // check if TOTP INPUT is needed: if not -> redirect to login page
      LoginAPI.totp(backend, '')
        .then(() => {
          // nothing
        })
        .catch((ex) => {
          if (ex.response.status === 403 || ex.response.status === 401) {
            (window as Window).location = `/${BASE_URL}/login`;
          }
        });
    }
  }, []);

  // validate form input
  const handleFormValidation = () => {
    const error = Validation.validateRecordWithConfig(
      {
        totp,
      },
      validationConfig,
    );

    setValidationState(error);

    return Object.keys(error).length === 0;
  };

  // handle submit, submit TOTP code
  const handleTotpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setError(null);
      await LoginAPI.totp(backend, totp);
      // redirect to desk after successful login
      (window as Window).location = DEFAULT_REDIRECT_LOCATION;
    } catch (ex) {
      setTotp('');
      setError(t('login:errors.invalidCode'));
    }
  };

  // render form
  return (
    <Box>
      <Form onSubmit={handleTotpSubmit} customValidation={handleFormValidation}>
        <FormRow margin="large">
          <Headline size="large">
            {setup ? t('login:headlines.totpSetup') : t('login:headlines.totp')}
          </Headline>
        </FormRow>

        {setup && (
          <>
            <FormRow>
              <Typography variant="body1">{t('login:texts.totpSetup')}</Typography>
            </FormRow>
            <FormRow margin={'large'}>
              <img src="/api/login/qrcode" alt="QR-Code" />
            </FormRow>
          </>
        )}

        <FormRow>
          <TextField
            placeholder={t('login:inputs.code')}
            required={true}
            value={totp}
            validationConfig={validationConfig.totp}
            errorMessage={validationState.totp}
            onChange={(evt) => {
              setValidationState({});
              setTotp(evt.target.value);
            }}
            autoFocus={true}
          />
        </FormRow>

        {error && (
          <FormRow>
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </FormRow>
        )}

        <FormRow>
          <Button variant="contained" color="primary" type="submit">
            {t('login:buttons.login')}
          </Button>
        </FormRow>
      </Form>

      <Box my={5} color="grey.600">
        <Typography variant="body1" color="textSecondary" align="right">
          {window.versionName}
        </Typography>
      </Box>
    </Box>
  );
};
